/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { FC, useState, Dispatch, SetStateAction, useEffect } from "react"
import { Row, Col, Checkbox } from "@duxton-web/ui"
import { OverflowTip } from "components/common"

interface FilterItem {
  value: string
  text: string
  subtext?: string
  className?: string
  unselectable?: boolean
}

export interface FilterDropdownProps {
  items: FilterItem[]
  filteredValue: string[]
  filterConfirmText?: string
  setVisible: Dispatch<SetStateAction<boolean>>
  onChange: (filteredValue: string[]) => void
  title?: string
}

export const FilterDropdown: FC<FilterDropdownProps> = ({
  items,
  filteredValue,
  filterConfirmText,
  setVisible,
  onChange,
  title,
}: FilterDropdownProps) => {
  const ALL = "ALL"
  const [checkedValues, setCheckedValues] = useState<string[]>([
    ...filteredValue,
  ])

  useEffect(() => {
    setCheckedValues([...filteredValue])
  }, [filteredValue])

  const handleClickItem = (clickedValue: string) => {
    let newCheckedValues = [...checkedValues]
    // uncheck
    if (newCheckedValues.includes(clickedValue)) {
      if (clickedValue !== ALL) {
        // prevent ALL to be unchecked
        newCheckedValues = newCheckedValues.filter(
          checkedValue => checkedValue !== clickedValue,
        )
      }
      // check ALL when all of item is unchecked
      if (newCheckedValues.length === 0) {
        newCheckedValues = [ALL]
      }
      // check
    } else if (clickedValue === ALL) {
      newCheckedValues = [ALL] // clear other checkbox when ALL got checked
    } else {
      if (newCheckedValues.includes(ALL)) {
        // clear ALL when other checkbox got checked
        newCheckedValues = newCheckedValues.filter(
          checkedValue => checkedValue !== ALL,
        )
      }
      newCheckedValues.push(clickedValue)
    }
    setCheckedValues(newCheckedValues)
  }

  return (
    <React.Fragment>
      <ul className="dui-dropdown-menu dui-dropdown-menu-without-submenu dui-dropdown-menu-root dui-dropdown-menu-vertical">
        {items.map(item => (
          <li
            key={item.value}
            className={`dui-dropdown-menu-item ${
              checkedValues.includes(item.value)
                ? "dui-dropdown-menu-item-selected"
                : ""
            } ${item.className ?? ""}`}
          >
            <Row
              align="middle"
              data-testid="row-list"
              onClick={() => !item.unselectable && handleClickItem(item.value)}
              css={css`
                flex-wrap: nowrap;
                width: 100%;
              `}
              gutter={16}
            >
              <Col>
                <Checkbox
                  css={css`
                    pointer-events: none;
                  `}
                  checked={checkedValues.includes(item.value)}
                />
              </Col>
              <OverflowTip
                css={css`
                  flex-grow: 1;
                `}
              >
                {item.text}
              </OverflowTip>
              {item.subtext && (
                <Col
                  css={css`
                    font-size: 12px;
                    color: #707070;
                    margin-left: 8px;
                    white-space: nowrap;
                  `}
                >
                  {item.subtext}
                </Col>
              )}
            </Row>
          </li>
        ))}
      </ul>
      <div
        className="dui-table-filter-dropdown-btns dui-table-filter-dropdown-btns-custom"
        style={{ cursor: "pointer" }}
        onClick={() => {
          setVisible(false)
          onChange(checkedValues)
        }}
      >
        <a
          className="dui-table-filter-dropdown-link confirm"
          id={title ? `${title}-filter-confirm-btn` : `filter-confirm-btn`}
        >
          {filterConfirmText || "OK"}
        </a>
      </div>
    </React.Fragment>
  )
}
